import classNames from 'classnames';
import React, { FC, useCallback, useMemo } from 'react';

import { AccordionProps, TitleContent } from '../models';
import { AccordionContent } from './accordion-content';
import { AccordionTitle } from './accordion-title';

interface AccordionItemProps
  extends Omit<AccordionProps, 'items' | 'onChange' | 'title'> {
  indexValue: number;
  isOpen: boolean;
  handleTogglerAccordionItem: () => void;
  title: TitleContent;
  itemsLength: number;
  disabled?: boolean;
}

export const AccordionItem: FC<AccordionItemProps> = ({
  semanticVariant,
  indexValue,
  isOpen,
  itemsLength,
  headlineTag,
  openIcon,
  closeIcon,
  title,
  className,
  handleTogglerAccordionItem,
  children,
  disabled,
  ...rest
}) => {
  const Tag = useMemo(
    () => (semanticVariant === 'div' ? 'section' : 'div'),
    [semanticVariant]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleTogglerAccordionItem();
      }
    },
    [handleTogglerAccordionItem]
  );

  return (
    <Tag
      className={classNames('cds-accordion__item', className, {
        active: isOpen,
        'cds-accordion__item--disabled': disabled,
      })}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      <AccordionTitle
        tabIndex={-1}
        isOpen={isOpen}
        headlineTag={headlineTag}
        openIcon={openIcon}
        closeIcon={closeIcon}
        semanticVariant={semanticVariant}
        onToggle={handleTogglerAccordionItem}
        title={title}
        disabled={disabled}
      />
      <AccordionContent
        semanticVariant={semanticVariant}
        index={indexValue}
        itemsLength={itemsLength}
        isOpen={isOpen}
      >
        {children}
      </AccordionContent>
    </Tag>
  );
};
